import { useEffect, useState, useSyncExternalStore } from "react";

/** The topbar search box: one query, owned by the module that is on screen. */
export type HubSearchSection = "home" | "mail" | "chat" | "calendar" | "meetings" | "tasks" | "ai" | "other";

type HubSearchState = { section: HubSearchSection; query: string };

let state: HubSearchState = { section: "other", query: "" };
const listeners = new Set<() => void>();

function emit(next: HubSearchState) {
  if (next.section === state.section && next.query === state.query) return;
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hubSearchSection(pathname: string): HubSearchSection {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/mail")) return "mail";
  if (pathname.startsWith("/chat")) return "chat";
  if (pathname.startsWith("/calendar")) return "calendar";
  if (pathname.startsWith("/meetings")) return "meetings";
  if (pathname.startsWith("/tasks")) return "tasks";
  if (pathname.startsWith("/ai")) return "ai";
  return "other";
}

/** Switching modules clears the box, unless the query was handed to the new module first. */
export function enterHubSearchSection(section: HubSearchSection) {
  if (section !== state.section) emit({ section, query: "" });
}

/** Signed out (the shell unmounted): forget the query. */
export function resetHubSearch() {
  emit({ section: "other", query: "" });
}

export function setHubSearchQuery(query: string, section: HubSearchSection = state.section) {
  emit({ section, query });
}

/** The query for `section`; empty while the box still holds another module's query. */
export function useHubSearchQuery(section: HubSearchSection): string {
  return useSyncExternalStore(
    subscribe,
    () => (state.section === section ? state.query : ""),
    () => "",
  );
}

export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function searchTokens(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean);
}

function collectText(value: unknown, out: string[], depth = 0) {
  if (value == null || depth > 5) return;
  if (typeof value === "string") {
    out.push(value);
    return;
  }
  if (typeof value === "number") {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectText(item, out, depth + 1);
    return;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    collectText(record.name, out, depth + 1);
    collectText(record.email, out, depth + 1);
    collectText(record.displayName, out, depth + 1);
  }
}

/** True when every word of the query appears somewhere in the given fields (case-insensitive). */
export function matchesHubSearch(query: string, ...fields: unknown[]): boolean {
  const tokens = searchTokens(query);
  if (!tokens.length) return true;
  const parts: string[] = [];
  for (const field of fields) collectText(field, parts);
  const haystack = parts.join(" \n ").toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}
